/**
 * Evidence of an email_delivery_failed case (#3786): which address the email
 * did not reach, why, and which customers have that address on file, linked so
 * the foreman can fix it.
 */
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Stack, Typography } from '@mui/material';

interface EvidenceCustomer {
  type: 'person' | 'company';
  id: string;
  name: string;
}

function text(evidence: Record<string, unknown> | null, key: string): string | null {
  const v = evidence?.[key];
  return typeof v === 'string' && v !== '' ? v : null;
}

function customersOf(evidence: Record<string, unknown> | null): EvidenceCustomer[] {
  const raw = evidence?.['customers'];
  if (!Array.isArray(raw)) return [];
  return raw.filter((c: unknown): c is EvidenceCustomer => {
    if (!c || typeof c !== 'object') return false;
    const { type, id, name } = c as Record<string, unknown>;
    return (type === 'person' || type === 'company') && typeof id === 'string' && typeof name === 'string';
  });
}

export function EmailDeliveryEvidence({
  evidence,
  detailed = false,
}: {
  evidence: Record<string, unknown> | null;
  /** The dialog shows subject, reason and customer links; the card only the summary. */
  detailed?: boolean;
}) {
  const { t } = useTranslation('exceptions');
  const recipient = text(evidence, 'recipientEmail');
  const status = text(evidence, 'deliveryStatus');
  const subject = text(evidence, 'subject');
  const reason = text(evidence, 'reason');
  const customers = customersOf(evidence);

  return (
    <Stack spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {recipient ? t('email.recipient', { address: recipient }) : ''}
        {recipient && status ? ' · ' : ''}
        {status ? t(`email.status.${status}`, { defaultValue: status }) : ''}
      </Typography>
      {detailed && subject && (
        <Typography variant="body2" color="text.secondary">{t('email.subject', { subject })}</Typography>
      )}
      {detailed && reason && (
        <Typography variant="body2" color="text.secondary">{t('email.reason', { reason })}</Typography>
      )}
      {detailed && (
        customers.length > 0 ? (
          <Typography variant="body2" component="div">
            {t('email.customers')}{' '}
            {customers.map((c, i) => (
              <span key={c.id}>
                {i > 0 ? ', ' : ''}
                <Link component={RouterLink} to={`/parties/${c.type === 'person' ? 'persons' : 'companies'}/${c.id}`}>
                  {c.name}
                </Link>
              </span>
            ))}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">{t('email.noCustomer')}</Typography>
        )
      )}
    </Stack>
  );
}
